import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useCardView from '../../../../hooks/useCardView';
import classnames from 'classnames';
import { MemberPriceContainerV2 } from './MemberPriceContainerV2';
import FormatterV1 from '../V1/FormatterV1';
import { SaveWasContainerV1 } from '../V1/SaveWasContainerV1';
import { DeliveryTooltip } from './DeliveryTooltip';
import { listerClickThrough } from '../../../../actions/lister-actions';

const PricePodV2 = ({
	classes,
	viewModel: {
		NowPrice,
		WasPrice,
		SavePrice,
		RetailPriceIncVat,
		RetailPriceSavings,
		MemberPrice,
		ProductMemberPriceFinanceQuote,
		PriceWithDelivery,
		MemberUpfrontPaymentAmount,
		upfrontPaymentAmount,
		UpfrontPaymentPercentage,
	} = {},
	inStock,
	FullTitle,
	Promotions: { FinancePromotions } = {},
	Code,
	ColourId,
	State,
	exVat,
	enhancedCardLayout = false,
	product,
}) => {
	const dispatch = useDispatch();
	const { isDealsPage } = useSelector((state) => state);
	const { isListView } = useCardView();
	if (!NowPrice) {
		return null;
	}

	const dealAvailable = SavePrice > 0 || RetailPriceSavings > 0;

	const displayWasPrice = WasPrice > 0 && SavePrice > 0;
	const displayMemberPrice = MemberPrice > 0;

	const was = displayWasPrice ? WasPrice : RetailPriceIncVat;
	const save = displayWasPrice ? SavePrice : RetailPriceSavings;
	const freeDelivery = PriceWithDelivery === NowPrice;
	const memberSaving = NowPrice - MemberPrice;
	const hasExtraSavings = was > 0 && save > 0;

	//enhanced card layout
	return enhancedCardLayout ? (
		<div
			className={classnames(classes, {
				'flex mt-2 enhanced-layout-price-pod': enhancedCardLayout,
				'flex-row-reverse': displayMemberPrice,
			})}
			style={{ gap: '4px' }}>
			{displayMemberPrice && (
				<div
					className="memberprice-container px-3 py-2 rounded relative w-1/2 flex flex-col"
					data-testid="price-member"
					style={{ gap: '4px' }}>
					<a
						href={product.FullProductUrl}
						onClick={(event) => {
							event.preventDefault();
							window.location.assign(product.FullProductUrl);

							dispatch(listerClickThrough(product.Code));
						}}>
						<div className="flex flex-col text-title lg:text-xs xl:items-baseline">
							<span className="member-price-title text-xs z-10 text-cta-mainhover mb-1 leading-none">
								AO member price
							</span>
							<div
								className={classnames(
									'flex flex-col sm:flex-row sm:items-baseline md:flex-col',
									{
										'xl:flex-row': !hasExtraSavings,
									}
								)}
								style={{ gap: '4px' }}>
								<span className="member-price text-lg md:text-lg 2xl:block text-cta-mainhover leading-none xl:leading-none">
									{FormatterV1.format(MemberPrice)}
								</span>
								<div className="text-membersave text-caption font-bold text-deals leading-none">
									{hasExtraSavings
										? `${FormatterV1.format(
												memberSaving
										  )}\u00A0Extra\u00A0Saving`
										: `Save ${FormatterV1.format(memberSaving)}`}
								</div>
							</div>
						</div>
					</a>
					<div className="text-body-xs text-water font-bold leading-snug">
						<span>Free delivery</span>
						<DeliveryTooltip />
					</div>
				</div>
			)}

			{displayMemberPrice ? (
				<div
					className="flex flex-col items-baseline pr-3 xl:px-3 w-1/2 xl:border xl:border-solid xl:border-gray-30 xl:rounded py-2"
					style={{ gap: '4px' }}>
					<a
						href={product.FullProductUrl}
						onClick={(event) => {
							event.preventDefault();
							window.location.assign(product.FullProductUrl);

							dispatch(listerClickThrough(product.Code));
						}}>
						<div
							className="non-member-price-title text-title-sm md:text-xs leading-none mb-1"
							style={{ lineHeight: '1' }}>
							Non-member price
						</div>
						<div className="flex flex-col xl:items-baseline">
							<div
								className={classnames('text-left', {
									'h-6': was && save,
									'text-deals': dealAvailable,
									'text-title-lg main-price': !isDealsPage,
									'text-primary md:mr-0': displayMemberPrice,
								})}
								data-testid="price-now">
								{FormatterV1.format(NowPrice)}
							</div>

							{was > 0 && save > 0 && (
								<SaveWasContainerV1
									{...{
										was,
										save,
										displayWasPrice,
										displayMemberPrice,
										enhancedCardLayout,
									}}
								/>
							)}
						</div>
					</a>
					<div className="text-body-xs text-water font-bold leading-snug">
						<span>
							{freeDelivery
								? 'Free Delivery'
								: `${FormatterV1.format(PriceWithDelivery)} with delivery`}
						</span>
						<DeliveryTooltip />
					</div>
				</div>
			) : (
				<>
					<div className="flex mt-2 lg:mt-0 2xl:mt-2 items-baseline flex-col">
						<a
							href={product.FullProductUrl}
							onClick={(event) => {
								event.preventDefault();
								window.location.assign(product.FullProductUrl);

								dispatch(listerClickThrough(product.Code));
							}}>
							<div
								className={`flex flex-col xl:flex-row ${
									exVat ? '' : 'items-baseline'
								}`}>
								<div
									className={classnames('text-left pr-1', {
										'h-6 md:h-auto': was && save,
										'text-deals': dealAvailable,
										'text-title-lg lg:text-xl main-price mb-1': !isDealsPage,
										'text-primary md:mr-0 pl-2': displayMemberPrice,
									})}
									data-testid="price-now">
									{FormatterV1.format(NowPrice)}
								</div>

								{was > 0 && save > 0 && (
									<SaveWasContainerV1
										{...{ was, save, displayWasPrice, displayMemberPrice }}
									/>
								)}
							</div>
						</a>
						{exVat && <span className="text-caption">Ex VAT</span>}
						<div className="text-body-xs text-water font-bold">
							<span>
								{freeDelivery
									? 'Free Delivery'
									: `${FormatterV1.format(PriceWithDelivery)} with delivery`}
							</span>
							<DeliveryTooltip />
						</div>
					</div>
				</>
			)}
		</div>
	) : (
		//standard price pod layout
		<div
			className={classnames(classes, {
				'mt-2 md:mt-0': !isDealsPage,
				'flex flex-col mb-1': isDealsPage,
				'lg:justify-end': isDealsPage && isListView,
			})}>
			{displayMemberPrice && (
				<MemberPriceContainerV2
					memberPrice={MemberPrice}
					nowPrice={NowPrice}
					was={was}
					save={save}
					ProductMemberPriceFinanceQuote={ProductMemberPriceFinanceQuote}
					inStock={inStock}
					Code={Code}
					ColourId={ColourId}
					financeType={null}
					State={State}
					FinancePromotions={FinancePromotions}
					productTitle={FullTitle}
					memberPricePod={true}
					memberUpfrontPaymentAmount={MemberUpfrontPaymentAmount}
					upfrontPaymentAmount={upfrontPaymentAmount}
					upfrontPaymentPercentage={UpfrontPaymentPercentage}
				/>
			)}

			{displayMemberPrice ? (
				<div className="flex flex-col mt-2 2xl:mt-2 items-baseline border border-gray-30 rounded border-solid px-3 py-2">
					<span className="text-title-sm mb-1">Non-member price</span>
					<div className="flex flex-col xl:flex-row xl:items-baseline">
						<div
							className={classnames('text-left', {
								'h-6 md:h-auto': was && save,
								'text-deals': dealAvailable,
								'text-display-lg': isDealsPage,
								'text-title-lg lg:text-xl main-price mb-1': !isDealsPage,
								'lg:text-title-lg': isDealsPage && isListView,
								'text-primary md:mr-0': displayMemberPrice,
							})}
							data-testid="price-now">
							{FormatterV1.format(NowPrice)}
						</div>

						{was > 0 && save > 0 && (
							<SaveWasContainerV1
								{...{ was, save, displayWasPrice, displayMemberPrice }}
							/>
						)}
					</div>
					<div className="flex flex-row items-baseline">
						<span className="text-body-xs text-water font-bold">
							{freeDelivery
								? 'Free Delivery'
								: `${FormatterV1.format(PriceWithDelivery)} with delivery`}
						</span>
						<DeliveryTooltip />
					</div>
				</div>
			) : (
				<>
					<div className="flex mt-2 lg:mt-0 2xl:mt-2 items-baseline flex-col border border-gray-30 rounded border-solid p-2">
						<div
							className={`flex flex-col xl:flex-row ${
								exVat ? '' : 'items-baseline'
							}`}>
							<div
								className={classnames('text-left pr-1', {
									'h-6 md:h-auto': was && save,
									'text-deals': dealAvailable,
									'text-display-lg': isDealsPage,
									'text-title-lg lg:text-xl main-price mb-1': !isDealsPage,
									'lg:text-title-lg': isDealsPage && isListView,
									'text-primary md:mr-0 pl-2': displayMemberPrice,
								})}
								data-testid="price-now">
								{FormatterV1.format(NowPrice)}
							</div>

							{was > 0 && save > 0 && (
								<SaveWasContainerV1
									{...{ was, save, displayWasPrice, displayMemberPrice }}
								/>
							)}
						</div>
						{exVat && <span className="text-caption">Ex VAT</span>}
						<div className="flex flex-row items-baseline">
							<span className="text-body-xs text-water font-bold">
								{freeDelivery
									? 'Free Delivery'
									: `${FormatterV1.format(PriceWithDelivery)} with delivery`}
							</span>
							<DeliveryTooltip />
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default PricePodV2;
